import React from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MissionNavigator from "./MissionNavigator";
import { getColorFromString, MissionColor } from "../../../utils/mission-color";
import { getNavigatorDescription } from "../../../interfaces/robot/navigator";
import Mission from "../../../interfaces/robot/missions/mission";
import { validateName } from "../../../validation/mission-validation";

interface MissionFormContentProps {
  formMission: Mission;
  onMissionChange: (updated: Partial<Mission>) => void;
  roomId?: string;
  navigatorAsName?: boolean;
  bottomButtons?: React.ReactNode;
  includePadding?: boolean;
}

const MissionFormContent: React.FC<MissionFormContentProps> = ({
  formMission,
  onMissionChange,
  roomId,
  navigatorAsName,
  bottomButtons,
  includePadding,
}) => {
  const { t } = useTranslation();

  const nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    onMissionChange({ name: newValue });
  };

  const colorChangeHandler = (event: SelectChangeEvent<MissionColor>) => {
    onMissionChange({ color: event.target.value as MissionColor });
  };

  const updateMissionHandler = (updatedMission: Partial<Mission>) => {
    onMissionChange(updatedMission);
  };

  const nameValidationError = validateName(formMission.name).find((vR) => !vR.isValid);

  return (
    <Stack>
      <MissionNavigator
        mission={formMission}
        onMissionChange={updateMissionHandler}
        roomId={roomId}
        includePadding={includePadding}
      >
        <Stack>
          <Typography variant="subtitle2">{t("mission-name-label")}</Typography>
          <TextField
            value={navigatorAsName ? getNavigatorDescription(formMission.mode) : formMission.name}
            onChange={nameChangeHandler}
            error={!((formMission.name ?? "").trim().length > 0) || nameValidationError != null}
            helperText={
              (formMission.name ?? "").trim().length > 0 ? nameValidationError?.error : t("mission-name-required")
            }
            fullWidth
            disabled={navigatorAsName}
            variant="standard"
          />
        </Stack>
        <FormControl fullWidth variant="standard">
          <Typography variant="subtitle2">{t("mission-color-label")}</Typography>
          <Select
            size="medium"
            value={formMission.color}
            onChange={colorChangeHandler}
            variant="standard"
            data-testid="mission-color-input"
            labelId="mission-color-input"
            renderValue={(value) => (
              <Box
                sx={(theme) => ({
                  backgroundColor: getColorFromString(value, "0.3"),
                  height: theme.spacing(3),
                  border: 1,
                })}
              />
            )}
          >
            {Object.values(MissionColor).map((color) => (
              <MenuItem
                key={color}
                value={color}
                sx={(theme) => ({
                  backgroundColor: getColorFromString(color, "0.3"),
                  height: theme.spacing(4),
                  ":hover": { backgroundColor: getColorFromString(color, "0.5") },
                })}
              />
            ))}
          </Select>
        </FormControl>
        {bottomButtons != null && <Box pb={2} justifyContent="flex-end" display="flex" >{bottomButtons}</Box>}
      </MissionNavigator>
    </Stack>
  );
};

export default MissionFormContent;
